"use client";

import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import Image from "next/image";
import { ORBIT_LINES, ORBIT_VIEW_BOX } from "./orbitLines";

const JAR_IMAGE_URL = "https://res.cloudinary.com/dirscuuhp/image/upload/f_auto,q_auto/v1788810909/new_design/oestra-jar_omfv5q.webp";
const JAR_WIDTH = 1848;
const JAR_HEIGHT = 1876;

const LINE_GROW_DURATION = 0.8;
const LINE_STAGGER = 0.004;

type OrbitJarProps = {
    sizes: string;
};

export const OrbitJar = ({ sizes }: OrbitJarProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, amount: 0.5 });
    const reduceMotion = Boolean(useReducedMotion());

    return (
        <div ref={ref} className="relative">
            <Image
                src={JAR_IMAGE_URL}
                alt="Oestra® prescription bioidentical hormone cream by Inner Balance"
                width={JAR_WIDTH}
                height={JAR_HEIGHT}
                sizes={sizes}
                loading="lazy"
                className="h-auto w-full"
            />
            <svg
                viewBox={ORBIT_VIEW_BOX}
                fill="none"
                aria-hidden="true"
                className="pointer-events-none absolute left-[5.97%] top-[24.17%] h-auto w-[84.46%] stroke-primary500"
            >
                {ORBIT_LINES.map(([x1, y1, x2, y2, opacity], index) => (
                    <motion.path
                        key={index}
                        d={`M${x1} ${y1}L${x2} ${y2}`}
                        strokeOpacity={opacity}
                        strokeWidth={1.92333}
                        initial={reduceMotion ? false : { pathLength: 0 }}
                        animate={reduceMotion || isInView ? { pathLength: 1 } : undefined}
                        transition={{
                            duration: reduceMotion ? 0 : LINE_GROW_DURATION,
                            ease: "easeOut",
                            delay: reduceMotion ? 0 : index * LINE_STAGGER,
                        }}
                    />
                ))}
            </svg>
            {/* Lower right of the jar, cropped as in Figma, so the near side of the ring passes behind it */}
            <Image
                src={JAR_IMAGE_URL}
                alt=""
                aria-hidden="true"
                width={JAR_WIDTH}
                height={JAR_HEIGHT}
                sizes={sizes}
                loading="lazy"
                className="pointer-events-none absolute inset-0 h-auto w-full [clip-path:polygon(26.99%_56.36%,55.02%_54.51%,54.43%_45.83%,96.43%_43.05%,100%_93.75%,29.86%_98.39%)]"
            />

            {/* Badge */}
            <Image
                src="https://res.cloudinary.com/dirscuuhp/image/upload/v1790670728/new_design/badge_n7aa5j.svg"
                alt=""
                width={116}
                height={134}
                sizes="116px"
                loading="lazy"
                className="absolute bottom-0 lg:bottom-16 right-0 lg:right-2 object-contain object-top"
            />
        </div>
    );
};
